import React, {useContext, useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
// Firebase
import { FirebaseContext } from "../../firebase"
import Button from "../elements/button/Button";
import {RiCheckLine, RiCloseLine, RiLoader4Line} from "react-icons/ri";
import {capitalizeFirstLetter} from "../../utils/stringUtils";
import InputText from "../forms/input/InputText";
import {PlayNotifications} from "../../redux/notifications/PlayNotifications";

const SPECIFIC_BACKEND_PATHS = [
  {value: 'collections-api-keys', label: 'Collections · Api keys'},
  {value: 'collections-assets', label: 'Collections · Assets'},
  {value: 'collections-contents', label: 'Collections · Contents'},
  {value: 'collections-custom-collections', label: 'Collections · Custom collections'},
  {value: 'collections-entities', label: 'Collections · Entities'},
  {value: 'collections-exports', label: 'Collections · Exports'},
  {value: 'collections-groups', label: 'Collections · Groups'},
  {value: 'collections-imports', label: 'Collections · Imports'},
  {value: 'collections-jobs', label: 'Collections · Jobs'},
  {value: 'collections-logs', label: 'Collections · Logs'},
  {value: 'collections-models', label: 'Collections · Models'},
  {value: 'collections-notifications', label: 'Collections · Notifications'},
  {value: 'collections-playlists', label: 'Collections · Playlists'},
  {value: 'collections-roles', label: 'Collections · Roles'},
  {value: 'collections-segments', label: 'Collections · Segments'},
  {value: 'collections-services', label: 'Collections · Services'},
  {value: 'collections-structures', label: 'Collections · Structures'},
  {value: 'collections-usages', label: 'Collections · Usages'},
  {value: 'collections-users', label: 'Collections · Users'},
  {value: 'collections-views', label: 'Collections · Views'},
  {value: 'collections-webhooks', label: 'Collections · Webhooks'},
  {value: 'modules-ai', label: 'Modules · AI'},
  {value: 'modules-algolia', label: 'Modules · Algolia'},
  {value: 'modules-auto', label: 'Modules · Auto'},
  {value: 'modules-backups', label: 'Modules · Backups'},
  {value: 'modules-cron', label: 'Modules · Cron'},
  {value: 'modules-metrics', label: 'Modules · Metrics'},
  {value: 'modules-script', label: 'Modules · Script'},
  {value: 'modules-search', label: 'Modules · Search'},
  {value: 'modules-sso', label: 'Modules · SSO'},
  {value: 'modules-sso-delete', label: 'Modules · SSO delete'},
  {value: 'extra', label: 'Extra'},
];

const DeployFrontForm = ({closeForm, project, type = 'front'}) => {
  // Translations
  const { t } = useTranslation(['common', 'messages', 'dashboard']);

  const dispatch = useDispatch();
  const firebase = useContext(FirebaseContext);

  const [formLoading, setFormLoading] = useState(false);
  const [branch, setBranch] = useState('');
  const [prerenderVersion, setPrerenderVersion] = useState(project.prerenderVersion === "2" ? "2" : "1");
  const [deploySource, setDeploySource] = useState('cloudbuild');
  const [specificPath, setSpecificPath] = useState(SPECIFIC_BACKEND_PATHS[0].value);

  const deploy = () => {
    if(type === 'front'){
      deployFront();
    } else if(type === 'all'){
      deployAll();
    } else if(type === 'trial'){
      deployTrialEnding();
    } else if(type === 'restoreFront'){
      redeployReactApp();
    } else if(type === 'specificBackend'){
      deploySpecificBackend();
    } else if(type === 'fullBackend'){
      deployFullBackend();
    }
  }

  const deployFront = () => {
    setFormLoading(true);
    const deployFrontFunction = firebase.functions.httpsCallable('deployReactApp');

    deployFrontFunction({
      branch: branch?.length > 0 ? branch : 'main',
      project_id: project.id,
      prerenderVersion: prerenderVersion === "2" ? "2" : "1"
    }).then((result) => {
      if(result.data?.success){
        dispatch(
          PlayNotifications(
            {
              display:true,
              theme:"success",
              message: t('success.Deploy-request-success', {ns: 'messages'}),
            }
          )
        );
        closeForm();
      } else {
        const error = result.data?.error ? result.data.error : null;
        console.error("Error deploy front: ", error);
        const errorMessage = result.data?.message ? result.data.message : 'Erreur';

        dispatch(
          PlayNotifications(
            {
              display: true,
              theme: "danger",
              message: errorMessage,
            }
          )
        );
      }

      setFormLoading(false);
    }).catch((error) => {
      console.error("Error deploy front: ", error);
      const errorMessage = error?.message ? error.message : 'Erreur';

      dispatch(
        PlayNotifications(
          {
            display: true,
            theme: "danger",
            message: errorMessage,
          }
        )
      );
      setFormLoading(false);
    });
  }

  const redeployReactApp = () => {
    setFormLoading(true);
    const deployFrontFunction = firebase.functions.httpsCallable('redeployReactApp');

    deployFrontFunction({
      project_id: project.id,
    }).then((result) => {
      if(result.data?.success){
        dispatch(
          PlayNotifications(
            {
              display:true,
              theme:"success",
              message: t('success.Deploy-request-success', {ns: 'messages'}),
            }
          )
        );
        closeForm();
      } else {
        const error = result.data?.error ? result.data.error : null;
        console.error("Error deploy front: ", error);
        const errorMessage = result.data?.message ? result.data.message : 'Erreur';

        dispatch(
          PlayNotifications(
            {
              display: true,
              theme: "danger",
              message: errorMessage,
            }
          )
        );
      }

      setFormLoading(false);
    }).catch((error) => {
      console.error("Error deploy front: ", error);
      const errorMessage = error?.message ? error.message : 'Erreur';

      dispatch(
        PlayNotifications(
          {
            display: true,
            theme: "danger",
            message: errorMessage,
          }
        )
      );
      setFormLoading(false);
    });
  }

  const deployTrialEnding = () => {
    setFormLoading(true);
    const deployFrontFunction = firebase.functions.httpsCallable('deployTrialEnding');

    deployFrontFunction({
      project_id: project.id,
    }).then((result) => {
      if(result.data?.success){
        dispatch(
          PlayNotifications(
            {
              display:true,
              theme:"success",
              message: t('success.Deploy-request-success', {ns: 'messages'}),
            }
          )
        );
        closeForm();
      } else {
        const error = result.data?.error ? result.data.error : null;
        console.error("Error deploy front: ", error);
        const errorMessage = result.data?.message ? result.data.message : 'Erreur';

        dispatch(
          PlayNotifications(
            {
              display: true,
              theme: "danger",
              message: errorMessage,
            }
          )
        );
      }

      setFormLoading(false);
    }).catch((error) => {
      console.error("Error deploy front: ", error);
      const errorMessage = error?.message ? error.message : 'Erreur';

      dispatch(
        PlayNotifications(
          {
            display: true,
            theme: "danger",
            message: errorMessage,
          }
        )
      );
      setFormLoading(false);
    });
  }

  const deployAll = () => {
    setFormLoading(true);
    const deployProject = firebase.functions.httpsCallable('deployProject');

    deployProject({
      branch: branch?.length > 0 ? branch : 'main',
      project_id: project.id,
      prerenderVersion: prerenderVersion === "2" ? "2" : "1",
      deploy_source: deploySource,
    }).then((result) => {
      if(result.data?.success){
        dispatch(
          PlayNotifications(
            {
              display:true,
              theme:"success",
              message: t('success.Deploy-request-success', {ns: 'messages'}),
            }
          )
        );
        closeForm();
      } else {
        const error = result.data?.error ? result.data.error : null;
        console.error("Error deploy front: ", error);
        const errorMessage = result.data?.message ? result.data.message : 'Erreur';

        dispatch(
          PlayNotifications(
            {
              display: true,
              theme: "danger",
              message: errorMessage,
            }
          )
        );
      }

      setFormLoading(false);
    }).catch((error) => {
      console.error("Error deploy front: ", error);
      const errorMessage = error?.message ? error.message : 'Erreur';

      dispatch(
        PlayNotifications(
          {
            display: true,
            theme: "danger",
            message: errorMessage,
          }
        )
      );
      setFormLoading(false);
    });
  }

  const deploySpecificBackend = () => {
    setFormLoading(true);
    const deploySpecificBackendFunction = firebase.functions.httpsCallable('deploySpecificBackendFunctions');

    deploySpecificBackendFunction({
      projects: [project.id],
      branch: branch?.length > 0 ? branch : 'main',
      path: specificPath,
      deploy_source: deploySource,
    }).then((result) => {
      if(result.data?.success){
        dispatch(
          PlayNotifications(
            {
              display:true,
              theme:"success",
              message: t('success.Deploy-request-success', {ns: 'messages'}),
            }
          )
        );
        closeForm();
      } else {
        const error = result.data?.error ? result.data.error : null;
        console.error("Error deploy specific backend: ", error);
        const errorMessage = result.data?.message ? result.data.message : 'Erreur';

        dispatch(
          PlayNotifications(
            {
              display: true,
              theme: "danger",
              message: errorMessage,
            }
          )
        );
      }

      setFormLoading(false);
    }).catch((error) => {
      console.error("Error deploy specific backend: ", error);
      const errorMessage = error?.message ? error.message : 'Erreur';

      dispatch(
        PlayNotifications(
          {
            display: true,
            theme: "danger",
            message: errorMessage,
          }
        )
      );
      setFormLoading(false);
    });
  }

  const deployFullBackend = () => {
    setFormLoading(true);
    // `all: true` force un redéploiement complet du backend, peu importe
    // `backend_current_commit` — utile pour relancer un déploiement resté bloqué
    // (ex: chaîne interrompue suite à l'échec d'un groupe de fonctions).
    const backendDeploymentRequestFunction = firebase.functions.httpsCallable('backendDeploymentRequest');

    backendDeploymentRequestFunction({
      project_id: project.id,
      branch: branch?.length > 0 ? branch : 'main',
      all: true,
      deploy_source: deploySource,
    }).then((result) => {
      if(result.data?.success){
        dispatch(
          PlayNotifications(
            {
              display:true,
              theme:"success",
              message: t('success.Deploy-request-success', {ns: 'messages'}),
            }
          )
        );
        closeForm();
      } else {
        const error = result.data?.error ? result.data.error : null;
        console.error("Error deploy full backend: ", error);
        const errorMessage = result.data?.message ? result.data.message : 'Erreur';

        dispatch(
          PlayNotifications(
            {
              display: true,
              theme: "danger",
              message: errorMessage,
            }
          )
        );
      }

      setFormLoading(false);
    }).catch((error) => {
      console.error("Error deploy full backend: ", error);
      const errorMessage = error?.message ? error.message : 'Erreur';

      dispatch(
        PlayNotifications(
          {
            display: true,
            theme: "danger",
            message: errorMessage,
          }
        )
      );
      setFormLoading(false);
    });
  }

  useEffect(() => {
    return () => {
      setBranch('');
    }
  }, []);

  return (
    <div
      className="fixed z-30 inset-0 flex justify-center items-center bg-gray-600 bg-opacity-50 overflow-y-auto h-full max-w-100vw w-full max-h-100vh py-10">
      <div
        className="relative p-5 pb-0 border shadow-lg rounded-md bg-white w-4/5 md:w-3/5 lg:w-2/5 max-h-full memoryScrollbar">
        <div className="flex items-center justify-between">
          <h2 className={`text-lg`}>
            {type === 'front' && t('projects.Deploy-frontend', {ns: 'dashboard'})}
            {type === 'all' && t('projects.Deploy', {ns: 'dashboard'})}
            {type === 'trial' && t('projects.Deploy-trial-ending', {ns: 'dashboard'})}
            {type === 'restoreFront' && t('projects.Deploy-frontend-restore', {ns: 'dashboard'})}
            {type === 'specificBackend' && t('projects.Deploy-specific-backend', {ns: 'dashboard', defaultValue: 'Déployer des fonctions spécifiques'})}
            {type === 'fullBackend' && t('projects.Deploy-full-backend', {ns: 'dashboard', defaultValue: 'Relancer tout le backend'})}
          </h2>
        </div>

        <div className="mt-3 flex flex-col gap-y-4">
          {["front", "all", "specificBackend", "fullBackend"].includes(type) &&
            <div>
              <InputText
                id="branch"
                name="branch"
                label={t('projects.form.branch.label', {ns: 'dashboard'})}
                placeHolder={t('projects.form.branch.placeholder', {ns: 'dashboard'})}
                state={branch}
                setstate={(e) => setBranch(e.target.value)}
                // loading={disabled}
              />
              <p className={`text-xs text-grayPrimary`}>
                {t('projects.form.branch.notice', {ns: 'dashboard'})}
              </p>
            </div>
          }

          {type === 'specificBackend' &&
            <div className={"space-y-1"}>
              <span>
                {t('projects.form.specificBackendPath.label', {ns: 'dashboard', defaultValue: 'Groupe de fonctions à déployer :'})}
              </span>
              <select
                className={"w-full border border-gray-300 rounded px-2 py-1.5 bg-white"}
                value={specificPath}
                onChange={(e) => setSpecificPath(e.target.value)}
              >
                {SPECIFIC_BACKEND_PATHS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          }

          {["front", "all"].includes(type) &&
            <>
              <div className={"space-y-1"}>
                <span>
                  {t('projects.form.prerenderVersion.label', {ns: 'dashboard', defaultValue: 'Version de la fonction de pré-rendu :'})}
                </span>
                <div className={"flex items-center gap-2"}>
                  <input
                    type={"radio"}
                    id={"prerenderVersion1"}
                    name={"prerenderVersion"}
                    value={"1"}
                    checked={prerenderVersion === "1"}
                    onChange={(e) => setPrerenderVersion(e.target.value)}
                  />
                  <label htmlFor={"prerenderVersion1"}>
                    {t('projects.form.prerenderVersion-gen1.label', {ns: 'dashboard', defaultValue: '1ère génération'})}
                  </label>
                </div>
                <div className={"flex items-center gap-2"}>
                  <input
                    type={"radio"}
                    id={"prerenderVersion2"}
                    name={"prerenderVersion"}
                    value={"2"}
                    checked={prerenderVersion === "2"}
                    onChange={(e) => setPrerenderVersion(e.target.value)}
                  />
                  <label htmlFor={"prerenderVersion2"}>
                    {t('projects.form.prerenderVersion-gen1.label', {ns: 'dashboard', defaultValue: '2nd génération'})}
                  </label>
                </div>
              </div>
            </>
          }

          {["all", "specificBackend", "fullBackend"].includes(type) &&
            <div className={"space-y-1"}>
              <span>
                {t('projects.form.deploySource.label', {ns: 'dashboard', defaultValue: 'Moteur de déploiement backend :'})}
              </span>
              <div className={"flex items-center gap-2"}>
                <input
                  type={"radio"}
                  id={"deploySourceCloudbuild"}
                  name={"deploySource"}
                  value={"cloudbuild"}
                  checked={deploySource === "cloudbuild"}
                  onChange={(e) => setDeploySource(e.target.value)}
                />
                <label htmlFor={"deploySourceCloudbuild"}>Cloud Build</label>
              </div>
              <div className={"flex items-center gap-2"}>
                <input
                  type={"radio"}
                  id={"deploySourceGitlab"}
                  name={"deploySource"}
                  value={"gitlab"}
                  checked={deploySource === "gitlab"}
                  onChange={(e) => setDeploySource(e.target.value)}
                />
                <label htmlFor={"deploySourceGitlab"}>GitLab CI</label>
              </div>
            </div>
          }
        </div>

        <div
          className="flex items-end justify-between border-t border-gray-200 pb-5 pt-2 mt-10 sticky top-0 bottom-0 bg-white">
          <div className="flex flex-col gap-4 min480:flex-row">
            <Button
              theme="simple"
              size="small"
              icon={<RiCloseLine className="w-4 h-4 mr-1"/>}
              actionType={"action"}
              action={() => closeForm()}
            >
              {t('action.Back', {ns: 'common'})}
            </Button>
          </div>

          <Button
            theme={formLoading ? 'disabled' : ''}
            size="small"
            icon={formLoading ? <RiLoader4Line className="w-4 h-4 ml-1 animate-spin"/> :
              <RiCheckLine className="w-4 h-4 ml-1"/>}
            iconPosition="right"
            actionType="action"
            action={() => deploy()}
          >
            {t('action.Confirm', {ns: 'common'})}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DeployFrontForm;